import {
    BaseSubChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/base-sub-chart';
import { AxisName } from '@src/slide/domain/ui-model/element-modeles/property/chart/axis/axis.interface';
import {
    DoubleValueAxisTrendlineProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/geometry/trendline';
import { DataLabelProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/data-label';
import { STDLblPos } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';
import type {
    ILegendMarker } from '@src/slide/domain/ui-model/element-modeles/property/chart/legend/legend.interface';
import type {
    IChartShapeProps,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-properties.interface';
import type {
    IChartSeries,
    ITrendline,

    IDataLabel,
    DataPoint,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart/sub-chart-properties.interface';
import type { IAxisProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/axis/axis.interface';
import type { IScatterChart } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart';
import type {
    IScatterChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/scatter-chart.interface';
import type {
    IScatterChartSeries,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart/scatter-chart.interface';
import type {
    IMarkerProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/geometry/marker/marker.interface';
import { IChartDataItem, IDataItemProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/data-item/data-item.interface';
import { CANVAS_PT_SCALE } from '@src/slide/infrastructure/module/independent/unit';
import { IThemeModel } from '../../../Interface/theme-model.interface';
import { getThemeStyleTransformOpt, getFirstThemeLineStyle } from '../theme';

export class ScatterChartProperty extends BaseSubChartProperty<IScatterChart> implements IScatterChartProperty {
    public get scatterStyle() {
        return this.uiData.scatterStyle;
    }

    public getLegendMarker(props: IChartShapeProps, marker?: IMarkerProperty): ILegendMarker {
        return {
            symbol: marker?.symbol as any,
            props: marker?.props || props,
        };
    }

    public getAxisValuesFromData(axis: IAxisProperty) {
        // 主要纵轴取values，否则取category
        if (axis.name === AxisName.PRIMARY_VERTICAL) {
            return {
                values: this.data.map(data => data.value).filter(i => !isNaN(i)),
            };
        }

        return {
            values: this.data.map(data => data.category as number).filter(i => !isNaN(i)),
        };
    }


    public getDataLabelProperty(dataLabel: IDataLabel, dataItem: IDataItemProperty) {
        return new ScatterDataLabelProperty(dataLabel, this.context).init(dataItem);
    }

    public getDefaultThemeStyle = (target: IChartSeries | DataPoint, index: number, colorPaletteId?: string, total?: number): IChartShapeProps['style'] => {
        const { style } = this.context.getChartModel();
        const { borderScale, accentStartIndex } = getThemeStyleTransformOpt(style);
        const color = this.getThemeColor(index + accentStartIndex, colorPaletteId, total);
        const themeLineStyle = getFirstThemeLineStyle(this.getThemeColorManager()?.currentThemeModel as IThemeModel);
        const noFill = target.props?.style.border?.noFill;
        return {
            border: {
                ...themeLineStyle,
                width: (themeLineStyle?.width || 0) * borderScale || 1.5 * CANVAS_PT_SCALE,
                ...(noFill ? {
                    noFill,
                } : {
                    color,
                    tdColor: color,
                }),
            },
        };
    };

    protected initFlattenData() {
        super.initFlattenData();

        this.data = convertCategoryToNumber(this.data);
    }

    protected getTrendlineProperty(trendline: ITrendline, ser: IChartSeries) {
        return new DoubleValueAxisTrendlineProperty(trendline, this.context).init(this.data, ser);
    }
}

class ScatterDataLabelProperty extends DataLabelProperty {
    public get labelPosition(): STDLblPos {
        return this.uiData.labelPosition || STDLblPos.r;
    }
}

// 双valueAxis的情况下，如果category的categoryPoint.value为非Number的字符串，就转成索引对应的数字
// 这种情况一般出现在 office里将 categoryAxis转成valueAxis但保留其内容，如柱形图转散点图
// 这里将最终的data的category做一个转换
export function convertCategoryToNumber<T extends IChartDataItem>(data: T[]): T[] {
    return data.map((item) => {
        let { category, originCategory, value } = item;

        if (typeof item.category !== 'number' && !!item.category) {
            const convertedCategory = item.categoryIndex + 1;
            category = convertedCategory;
            originCategory = convertedCategory;
        }

        if (typeof item.value !== 'number' && !!item.value) {
            value = 0;
        }

        return Object.assign(item, { category, originCategory, value });
    });
}
